/* eslint-disable @typescript-eslint/no-explicit-any */
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatSecondsToReadableTime(seconds: number): string {
  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

export function textToSlug(text: string): string {
  return text
    ?.toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .trim();
}

export function getCurrentWeekAndDay(topicId: string, modules: any) {
  let currentWeek, currentDay;

  // Search through weeks and days
  for (const week of modules?.weeks) {
    for (const day of week?.days) {
      if (day?.topics?.some((topic: any) => topic.id === Number(topicId))) {
        currentWeek = week.weekNumber;
        currentDay = day.dayNumber;
        return { currentWeek, currentDay };
      }
    }
  }
  return { currentWeek: null, currentDay: null };
}

export function isEmptyObject(obj: any) {
  return Object.keys(obj).length === 0;
}

export const getMediaType = (
  url: string
): "video" | "audio" | "image" | "link" => {
  if (!url) return "link";

  // Extract file extension from URL
  const extension = url.split(".").pop()?.toLowerCase() || "";

  // Video formats
  const videoExtensions = ["mp4", "webm", "ogg", "mov", "avi", "wmv"];
  if (videoExtensions.includes(extension)) return "video";

  // Audio formats
  const audioExtensions = ["mp3", "wav", "ogg", "m4a", "aac"];
  if (audioExtensions.includes(extension)) return "audio";

  // Image formats
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
  if (imageExtensions.includes(extension)) return "image";

  // Default to link
  return "link";
};

export const flattenMessage = (message: any) => {
  const formatted = structuredClone(message);
  formatMessage(formatted, 1);
  return formatted;
};

function formatMessage(reply: any, level: number) {
  if (!reply?.replies?.length) return;

  // If next level would be deeper than 3 → flatten
  if (level + 1 > 3) {
    reply.replies = reply.replies.flatMap(collectAllReplies);
    return;
  }

  // Otherwise keep traversing
  reply.replies.forEach((child: any) => {
    formatMessage(child, level + 1);
  });
}

// Recursively collect all children into a single-level array
function collectAllReplies(node: any): any[] {
  if (!node?.replies?.length) return [node];

  const childReplies = node.replies.flatMap(collectAllReplies);
  return [{ ...node, replies: [] }, ...childReplies];
}

// Function to check if a URL is an image
export const isImageUrl = (url: string) => {
  return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
};

// Function to extract user initials from name
export function getInitials(name: string | null | undefined): string {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) {
    return parts[0].substring(0, 1).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
